/**
 * Offline Storage Service
 * Provides methods for caching and retrieving data from IndexedDB
 *
 * Features:
 * - Cache entities with automatic expiration
 * - Master data caching with long TTL
 * - Gaushala-scoped data isolation
 */

import { offlineDb, CachedEntity, CachedMasterData, SyncMetadata } from './db';
import { OFFLINE_CONFIG, EntityType, MasterDataKey, MASTER_DATA_KEYS } from './constants';

// ============== ENTITY CACHING ==============

/**
 * Cache an entity (cattle, health record, etc.)
 */
export async function cacheEntity<T>(
  entityType: EntityType,
  entity: T & { id: number | string },
  gaushalaId: number,
  isTemp: boolean = false
): Promise<void> {
  const table = offlineDb.getCacheTable(entityType);

  await table.put({
    id: entity.id,
    isTemp,
    syncStatus: isTemp ? 'pending' : 'synced',
    data: entity,
    cachedAt: new Date(),
    gaushalaId,
  });

  console.log(`[OfflineStorage] Cached ${entityType}: ${entity.id}`, { isTemp });
}

/**
 * Cache multiple entities
 */
export async function cacheEntities<T>(
  entityType: EntityType,
  entities: (T & { id: number | string })[],
  gaushalaId: number
): Promise<void> {
  const table = offlineDb.getCacheTable(entityType);

  const cachedEntities: CachedEntity<T>[] = entities.map(entity => ({
    id: entity.id,
    isTemp: false,
    syncStatus: 'synced' as const,
    data: entity,
    cachedAt: new Date(),
    gaushalaId,
  }));

  await table.bulkPut(cachedEntities);

  console.log(`[OfflineStorage] Cached ${entities.length} ${entityType} entities`);
}

/**
 * Get a cached entity by ID
 */
export async function getCachedEntity<T>(
  entityType: EntityType,
  id: number | string
): Promise<T | null> {
  const table = offlineDb.getCacheTable(entityType);
  const cached = await table.get(id);

  if (!cached) return null;

  // Check if cache is expired
  const now = new Date().getTime();
  const cachedTime = cached.cachedAt.getTime();
  const isExpired = now - cachedTime > OFFLINE_CONFIG.ENTITY_CACHE_DURATION;

  if (isExpired) {
    console.log(`[OfflineStorage] Cache expired for ${entityType}: ${id}`);
    return null;
  }

  return cached.data as T;
}

/**
 * Get all cached entities for a gaushala
 */
export async function getCachedEntities<T>(
  entityType: EntityType,
  gaushalaId: number
): Promise<T[]> {
  const table = offlineDb.getCacheTable(entityType);

  const cached = await table
    .where('gaushalaId')
    .equals(gaushalaId)
    .toArray();

  // Filter out expired entries (but keep pending ones regardless of age)
  const now = new Date().getTime();
  const validEntries = cached.filter(entry => {
    if (entry.syncStatus === 'pending') return true; // Keep pending items
    const cachedTime = entry.cachedAt.getTime();
    return now - cachedTime <= OFFLINE_CONFIG.ENTITY_CACHE_DURATION;
  });

  return validEntries.map(entry => entry.data as T);
}

/**
 * Get pending (not synced) entities for a gaushala
 */
export async function getPendingEntities<T>(
  entityType: EntityType,
  gaushalaId: number
): Promise<T[]> {
  const table = offlineDb.getCacheTable(entityType);

  const pending = await table
    .where('[gaushalaId+syncStatus]')
    .equals([gaushalaId, 'pending'])
    .toArray();

  return pending.map(entry => entry.data as T);
}

/**
 * Remove a cached entity
 */
export async function removeCachedEntity(
  entityType: EntityType,
  id: number | string
): Promise<void> {
  const table = offlineDb.getCacheTable(entityType);
  await table.delete(id);
  console.log(`[OfflineStorage] Removed cached ${entityType}: ${id}`);
}

/**
 * Clear all cached entities for an entity type
 */
export async function clearEntityCache(entityType: EntityType): Promise<void> {
  const table = offlineDb.getCacheTable(entityType);
  await table.clear();
  console.log(`[OfflineStorage] Cleared all cached ${entityType}`);
}

// ============== MASTER DATA CACHING ==============

/**
 * Cache master data (breeds, species, etc.)
 */
export async function cacheMasterData(
  key: MasterDataKey,
  data: any[]
): Promise<void> {
  const now = new Date();
  const expiresAt = new Date(now.getTime() + OFFLINE_CONFIG.MASTER_DATA_CACHE_DURATION);

  await offlineDb.cachedMasterData.put({
    key,
    data,
    cachedAt: now,
    expiresAt,
  });

  console.log(`[OfflineStorage] Cached master data: ${key} (${data.length} items)`);
}

/**
 * Get cached master data
 */
export async function getCachedMasterData<T>(key: MasterDataKey): Promise<T[] | null> {
  const cached = await offlineDb.cachedMasterData.get(key);

  if (!cached) return null;

  // Check if expired
  const now = new Date();
  if (now > cached.expiresAt) {
    console.log(`[OfflineStorage] Master data expired: ${key}`);
    return null;
  }

  return cached.data as T[];
}

/**
 * Check if master data is cached and valid
 */
export async function isMasterDataCached(key: MasterDataKey): Promise<boolean> {
  const cached = await offlineDb.cachedMasterData.get(key);

  if (!cached) return false;

  const now = new Date();
  return now <= cached.expiresAt;
}

/**
 * Clear all master data cache
 */
export async function clearMasterDataCache(): Promise<void> {
  await offlineDb.cachedMasterData.clear();
  console.log('[OfflineStorage] Cleared all master data cache');
}

/**
 * Get all cached master data keys
 */
export async function getCachedMasterDataKeys(): Promise<MasterDataKey[]> {
  const all = await offlineDb.cachedMasterData.toArray();
  const now = new Date();

  return all
    .filter(item => now <= item.expiresAt)
    .map(item => item.key);
}

// ============== SYNC METADATA ==============

/**
 * Update sync metadata after successful sync
 */
export async function updateSyncMetadata(
  gaushalaId: number,
  entityType: EntityType,
  recordCount?: number
): Promise<void> {
  // Try to find existing metadata
  const existing = await offlineDb.syncMetadata
    .where('[gaushalaId+entityType]')
    .equals([gaushalaId, entityType])
    .first();

  if (existing && existing.id) {
    await offlineDb.syncMetadata.update(existing.id, {
      lastSyncAt: new Date(),
      recordCount,
    });
  } else {
    await offlineDb.syncMetadata.add({
      gaushalaId,
      entityType,
      lastSyncAt: new Date(),
      recordCount,
    });
  }

  console.log(`[OfflineStorage] Updated sync metadata: ${entityType} for gaushala ${gaushalaId}`);
}

/**
 * Get sync metadata for an entity type
 */
export async function getSyncMetadata(
  gaushalaId: number,
  entityType: EntityType
): Promise<SyncMetadata | null> {
  const metadata = await offlineDb.syncMetadata
    .where('[gaushalaId+entityType]')
    .equals([gaushalaId, entityType])
    .first();

  return metadata || null;
}

/**
 * Check if data needs to be refreshed from server
 */
export async function needsRefresh(
  gaushalaId: number,
  entityType: EntityType,
  maxAgeMs: number = OFFLINE_CONFIG.ENTITY_CACHE_DURATION
): Promise<boolean> {
  const metadata = await getSyncMetadata(gaushalaId, entityType);

  if (!metadata) return true;

  const now = new Date().getTime();
  const lastSync = metadata.lastSyncAt.getTime();

  return now - lastSync > maxAgeMs;
}

// ============== UTILITY FUNCTIONS ==============

/**
 * Get cache statistics
 */
export async function getCacheStats(): Promise<{
  entities: Record<EntityType, number>;
  masterData: number;
  pendingOperations: number;
  failedOperations: number;
}> {
  const [
    cattleCount,
    healthRecordCount,
    milkProductionCount,
    foodHistoryCount,
    medicineCount,
    inventoryCount,
    shedCount,
    dungTransactionCount,
    rfidScanCount,
    masterDataCount,
    pendingCount,
    failedCount,
  ] = await Promise.all([
    offlineDb.cachedCattle.count(),
    offlineDb.cachedHealthRecords.count(),
    offlineDb.cachedMilkProduction.count(),
    offlineDb.cachedFoodHistory.count(),
    offlineDb.cachedMedicine.count(),
    offlineDb.cachedInventory.count(),
    offlineDb.cachedSheds.count(),
    offlineDb.cachedDungTransactions.count(),
    offlineDb.cachedRfidScans.count(),
    offlineDb.cachedMasterData.count(),
    offlineDb.getPendingCount(),
    offlineDb.getFailedCount(),
  ]);

  return {
    entities: {
      cattle: cattleCount,
      healthRecord: healthRecordCount,
      milkProduction: milkProductionCount,
      foodHistory: foodHistoryCount,
      medicine: medicineCount,
      inventory: inventoryCount,
      stockTransaction: 0, // Shares with inventory
      shed: shedCount,
      dungTransaction: dungTransactionCount,
      rfidScan: rfidScanCount,
    },
    masterData: masterDataCount,
    pendingOperations: pendingCount,
    failedOperations: failedCount,
  };
}

// ============== EXPORTS ==============

export const offlineStorage = {
  // Entity caching
  cacheEntity,
  cacheEntities,
  getCachedEntity,
  getCachedEntities,
  getPendingEntities,
  removeCachedEntity,
  clearEntityCache,

  // Master data
  cacheMasterData,
  getCachedMasterData,
  isMasterDataCached,
  clearMasterDataCache,
  getCachedMasterDataKeys,

  // Sync metadata
  updateSyncMetadata,
  getSyncMetadata,
  needsRefresh,

  // Utilities
  getCacheStats,
};
